import React, { forwardRef, useImperativeHandle, useRef, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faMinus, faTimes } from '@fortawesome/pro-light-svg-icons';
import Combobox from '@shared/components/ui/AdvancedFilter/components/Combobox';
import { ModalCenter, ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { Button } from '@shared/components/ui/button';
import { Product } from '@shared/components/ui/Product';
import { Badge } from '@shared/components/ui/badge';
import { StepperInput } from '@shared/components/ui/StepperInput';
import { EnterIcon, EscIcon } from '@shared/components/ui/icons';
import { useToast } from '@shared/hooks/use-toast';
import type { MassivePackingGroup } from '../../models';
import type { LocationsByProductApiResponse } from '../../adapters/MassivePackingApiAdapter';

export interface PrepareOrdersModalRef {
  open: (
    groups: MassivePackingGroup[],
    locationsByProduct: Record<number, LocationsByProductApiResponse['data']>,
  ) => void;
  close: () => void;
}

interface PrepareOrdersModalProps {
  onConfirm: (payload: {
    orders: Array<{
      order_id: number;
      products: Array<{
        product_id: number;
        locations: Array<{
          location_id: number;
          quantity: number;
        }>;
      }>;
    }>;
  }) => void;
  onCancel?: () => void;
}

interface ProductLocationSelection {
  productId: number;
  productName: string;
  productCode?: string;
  imageHash?: string | null;
  requiredQuantity: number;
  locations: Array<{
    locationId: number;
    locationCode: string;
    stock: number;
    quantity: number;
    type?: {
      id: number;
      name: string;
    } | null;
  }>;
}

export const PrepareOrdersModal = forwardRef<PrepareOrdersModalRef, PrepareOrdersModalProps>(
  ({ onConfirm, onCancel }, ref) => {
    const { t } = useTranslation('massivePackingModule');
    const { toast } = useToast();
    const modalRef = useRef<ModalCenterRef>(null);
    const [groups, setGroups] = useState<MassivePackingGroup[]>([]);
    const [locationsByProduct, setLocationsByProduct] = useState<Record<number, LocationsByProductApiResponse['data']>>(
      {},
    );
    const [productSelections, setProductSelections] = useState<Map<number, ProductLocationSelection>>(new Map());

    useImperativeHandle(ref, () => ({
      open: (
        selectedGroups: MassivePackingGroup[],
        locations: Record<number, LocationsByProductApiResponse['data']>,
      ) => {
        setGroups(selectedGroups);
        setLocationsByProduct(locations);

        // Inicializar selecciones de productos
        const selections = new Map<number, ProductLocationSelection>();
        const uniqueProducts = new Map<number, { name: string; code?: string; imageHash?: string | null }>();

        selectedGroups.forEach((group) => {
          group.products.forEach((product) => {
            if (!uniqueProducts.has(product.productId)) {
              uniqueProducts.set(product.productId, {
                name: product.productName || '',
                code: product.productCode,
                imageHash: product.imageHash,
              });
            }
          });
        });

        uniqueProducts.forEach((info, productId) => {
          const totalQuantity = selectedGroups.reduce(
            (sum, group) =>
              sum +
              group.products
                .filter((p) => p.productId === productId)
                .reduce((s, p) => s + p.perOrderQuantity * group.orders.length, 0),
            0,
          );

          const availableLocations = locations[productId] || [];
          // Si el producto tiene una sola ubicación posible, preseleccionarla al abrir el modal
          const initialLocations =
            availableLocations.length === 1
              ? [
                  {
                    locationId: availableLocations[0].id,
                    locationCode: availableLocations[0].code,
                    stock: availableLocations[0].stock || 0,
                    quantity: 0,
                    type: availableLocations[0].type || null,
                  },
                ]
              : [];

          selections.set(productId, {
            productId,
            productName: info.name,
            productCode: info.code,
            imageHash: info.imageHash,
            requiredQuantity: totalQuantity,
            locations: initialLocations,
          });
        });

        setProductSelections(selections);
        modalRef.current?.openModal();
      },
      close: () => {
        modalRef.current?.closeModal();
        setProductSelections(new Map());
      },
    }));

    const handleAddLocation = (productId: number) => {
      const selection = productSelections.get(productId);
      if (!selection) return;

      const availableLocations = locationsByProduct[productId] || [];
      const usedLocationIds = new Set(selection.locations.map((loc) => loc.locationId));
      const nextLocation = availableLocations.find((loc) => !usedLocationIds.has(loc.id));

      if (nextLocation) {
        const newLocations = [
          ...selection.locations,
          {
            locationId: nextLocation.id,
            locationCode: nextLocation.code,
            stock: nextLocation.stock || 0,
            quantity: 0,
            type: nextLocation.type || null,
          },
        ];
        const newSelection = {
          ...selection,
          locations: newLocations,
        };
        setProductSelections(new Map(productSelections).set(productId, newSelection));
      }
    };

    const handleRemoveLocation = (productId: number, locationIndex: number) => {
      const selection = productSelections.get(productId);
      if (!selection) return;

      const newLocations = selection.locations.filter((_, index) => index !== locationIndex);
      const newSelection = {
        ...selection,
        locations: newLocations,
      };
      setProductSelections(new Map(productSelections).set(productId, newSelection));
    };

    const handleLocationChange = (productId: number, locationIndex: number, locationId: string) => {
      const selection = productSelections.get(productId);
      if (!selection) return;

      const location = locationsByProduct[productId]?.find((loc) => loc.id === Number(locationId));
      if (!location) return;

      const newLocations = [...selection.locations];

      // Si no existe la ubicación en el índice, crearla
      if (!newLocations[locationIndex]) {
        newLocations[locationIndex] = {
          locationId: location.id,
          locationCode: location.code,
          stock: location.stock || 0,
          quantity: 0,
          type: location.type || null,
        };
      } else {
        const currentQuantity = newLocations[locationIndex].quantity;

        // Calcular cantidad ya asignada en otras ubicaciones (excluyendo la actual)
        const quantityInOtherLocations = newLocations
          .filter((loc, idx) => idx !== locationIndex)
          .reduce((sum, loc) => sum + loc.quantity, 0);

        // El máximo es la cantidad requerida menos lo ya asignado en otras ubicaciones
        const maxQuantity = Math.max(0, selection.requiredQuantity - quantityInOtherLocations);

        newLocations[locationIndex] = {
          ...newLocations[locationIndex],
          locationId: location.id,
          locationCode: location.code,
          stock: location.stock || 0,
          quantity: Math.min(currentQuantity, maxQuantity),
          type: location.type || null,
        };
      }

      const newSelection = {
        ...selection,
        locations: newLocations,
      };
      setProductSelections(new Map(productSelections).set(productId, newSelection));
    };

    const handleQuantityChange = (productId: number, locationIndex: number, delta: number) => {
      const selection = productSelections.get(productId);
      if (!selection) return;

      const location = selection.locations[locationIndex];
      if (!location) return;

      const maxStock = location.stock === -1 ? Infinity : location.stock || 0;
      const newQuantity = Math.max(0, Math.min(maxStock, location.quantity + delta));

      const newLocations = [...selection.locations];
      newLocations[locationIndex] = {
        ...location,
        quantity: newQuantity,
      };

      const newSelection = {
        ...selection,
        locations: newLocations,
      };
      setProductSelections(new Map(productSelections).set(productId, newSelection));
    };

    const handleSetMaxQuantity = (productId: number, locationIndex: number) => {
      const selection = productSelections.get(productId);
      if (!selection) return;

      const location = selection.locations[locationIndex];
      if (!location) return;

      // Si el stock es ilimitado (-1), establecer la cantidad requerida del producto
      const maxQuantity = location.stock === -1 ? selection.requiredQuantity : location.stock || 0;

      const newLocations = [...selection.locations];
      newLocations[locationIndex] = {
        ...location,
        quantity: maxQuantity,
      };

      const newSelection = {
        ...selection,
        locations: newLocations,
      };
      setProductSelections(new Map(productSelections).set(productId, newSelection));
    };

    const handleConfirm = () => {
      try {
        // Validar que todos los productos tengan ubicaciones seleccionadas
        for (const group of groups) {
          for (const product of group.products) {
            const selection = productSelections.get(product.productId);
            if (!selection || selection.locations.length === 0) {
              toast({
                title: t('modals.prepare_orders.validation_error_title', {
                  defaultValue: 'Error de validación',
                }),
                description: `Debes seleccionar al menos una ubicación para el producto ${product.productName || product.productId}`,
                variant: 'destructive',
              });
              return;
            }

            const validLocations = selection.locations.filter((loc) => loc.quantity > 0);

            if (validLocations.length === 0) {
              toast({
                title: t('modals.prepare_orders.validation_error_title', {
                  defaultValue: 'Error de validación',
                }),
                description: `Debes asignar cantidades para el producto ${product.productName || product.productId}`,
                variant: 'destructive',
              });
              return;
            }
          }
        }

        // Construir payload con las selecciones
        const ordersPayload = groups.flatMap((group) =>
          group.orders.map((order) => ({
            order_id: order.id,
            products: group.products.map((product) => {
              const selection = productSelections.get(product.productId);
              if (!selection) {
                throw new Error(`Error al procesar el producto ${product.productName || product.productId}`);
              }

              // Filtrar ubicaciones con cantidad > 0
              const validLocations = selection.locations.filter((loc) => loc.quantity > 0);

              // Calcular la cantidad total seleccionada (para todos los pedidos)
              const totalSelectedQuantity = validLocations.reduce((sum, loc) => sum + loc.quantity, 0);

              // Distribuir la cantidad por pedido entre las ubicaciones seleccionadas
              // proporcionalmente según las cantidades seleccionadas
              const locationsPayload: Array<{
                location_id: number;
                quantity: number;
              }> = [];

              let remainingQuantity = product.perOrderQuantity;

              // Distribuir proporcionalmente
              for (let i = 0; i < validLocations.length; i++) {
                const loc = validLocations[i];
                const proportion = loc.quantity / totalSelectedQuantity;
                let quantityForOrder = Math.floor(product.perOrderQuantity * proportion);

                // Si es la última ubicación, asignar todo lo que queda
                if (i === validLocations.length - 1) {
                  quantityForOrder = remainingQuantity;
                } else {
                  remainingQuantity -= quantityForOrder;
                }

                if (quantityForOrder > 0) {
                  locationsPayload.push({
                    location_id: loc.locationId,
                    quantity: quantityForOrder,
                  });
                }
              }

              // Asegurar que al menos hay una ubicación con cantidad > 0
              if (locationsPayload.length === 0) {
                locationsPayload.push({
                  location_id: validLocations[0].locationId,
                  quantity: product.perOrderQuantity,
                });
              }

              return {
                product_id: product.productId,
                locations: locationsPayload,
              };
            }),
          })),
        );

        onConfirm({ orders: ordersPayload });
        modalRef.current?.closeModal();
      } catch (error: any) {
        toast({
          title: t('modals.prepare_orders.validation_error_title', {
            defaultValue: 'Error de validación',
          }),
          description:
            error?.message ||
            t('modals.prepare_orders.validation_error_description', {
              defaultValue: 'Error al procesar la preparación',
            }),
          variant: 'destructive',
        });
      }
    };

    const handleCancel = () => {
      onCancel?.();
      modalRef.current?.closeModal();
    };

    const productsList = useMemo(() => {
      return Array.from(productSelections.values());
    }, [productSelections]);

    const getLocationOptions = (productId: number) => {
      const availableLocations = locationsByProduct[productId] || [];
      const selection = productSelections.get(productId);
      const usedLocationIds = new Set(selection?.locations.map((loc) => loc.locationId) || []);

      return availableLocations
        .filter((loc) => !usedLocationIds.has(loc.id))
        .map((loc) => ({
          label: loc.code,
          value: String(loc.id),
        }));
    };

    const getTotalSelectedQuantity = (productId: number) => {
      const selection = productSelections.get(productId);
      return selection?.locations.reduce((sum, loc) => sum + loc.quantity, 0) || 0;
    };

    return (
      <ModalCenter
        ref={modalRef}
        title={t('modals.prepare_orders.title')}
        type="default"
        width="xxl"
        className="max-md:w-[calc(100%-2rem)] max-md:max-w-[calc(100%-2rem)]"
      >
        <div className="space-y-4 p-1 max-md:px-0">
          <p className="text-sm text-subtext">{t('modals.prepare_orders.subtitle')}</p>

          <div className="space-y-4 max-h-[60vh] overflow-y-auto max-md:max-h-[60vh]">
            {productsList.map((product) => {
              const availableLocations = locationsByProduct[product.productId] || [];
              const totalSelected = getTotalSelectedQuantity(product.productId);
              const isComplete = totalSelected >= product.requiredQuantity;

              // Obtener la primera ubicación o crear una vacía
              const firstLocation = product.locations[0] || {
                locationId: 0,
                locationCode: '',
                stock: 0,
                quantity: 0,
                type: null,
              };

              // Obtener ubicaciones ya seleccionadas (excluyendo la actual de la primera fila)
              const selectedLocationIds = new Set(
                product.locations
                  .map((loc) => loc.locationId)
                  .filter((id) => id > 0 && id !== firstLocation.locationId),
              );

              const allLocationOptions = (locationsByProduct[product.productId] || [])
                .filter((loc) => !selectedLocationIds.has(loc.id))
                .map((loc) => {
                  const stockText =
                    loc.stock === -1
                      ? t('modals.prepare_orders.unlimited_stock', {
                          defaultValue: 'Ilimitado',
                        })
                      : `${loc.stock || 0}un.`;
                  return {
                    label: `${loc.code} (${stockText})`,
                    value: String(loc.id),
                  };
                });

              const selectedLocation = locationsByProduct[product.productId]?.find(
                (loc) => loc.id === firstLocation.locationId,
              );

              // Calcular cantidad ya asignada en otras ubicaciones (excluyendo la primera)
              const quantityInOtherLocations = product.locations.slice(1).reduce((sum, loc) => sum + loc.quantity, 0);

              // El máximo es la cantidad requerida menos lo ya asignado en otras ubicaciones
              const maxQuantity = Math.max(0, product.requiredQuantity - quantityInOtherLocations);

              return (
                <div key={product.productId} className="border border-card-border rounded-lg p-4 max-md:p-3">
                  {/* Desktop: una fila horizontal | Mobile: varias filas */}
                  <div className="flex flex-col gap-3 w-full md:flex-row md:items-center md:justify-between md:gap-4">
                    {/* Fila 1: Producto + Badge — ancho flexible con truncamiento */}
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <Product
                        id={product.productId}
                        src={product.imageHash || ''}
                        name={product.productName}
                        code={product.productCode}
                        acronym={product.productName ? product.productName.charAt(0).toUpperCase() : '?'}
                        showTooltip={true}
                        enableLineClamp={true}
                      />
                      <Badge
                        variant="secondary"
                        className="text-xs bg-secondary/50 border-secondary text-foreground h-6 min-w-[1.5rem] flex items-center justify-center shrink-0 md:ml-auto"
                      >
                        {product.requiredQuantity}
                      </Badge>
                    </div>

                    {/* Controles: alineados a la derecha, sin ancho forzado en desktop */}
                    <div className="flex flex-col gap-2 w-full md:w-auto md:flex-row md:items-center md:gap-3 md:flex-shrink-0">
                      <div className="flex flex-col gap-2 max-md:w-full md:flex-row md:items-center md:gap-3 md:flex-nowrap">
                        {/* Combobox de ubicación */}
                        <div className="w-full md:w-64 flex-shrink-0">
                          <Combobox
                            options={allLocationOptions}
                            value={firstLocation.locationId > 0 ? String(firstLocation.locationId) : ''}
                            setValue={(value) => {
                              const selection = productSelections.get(product.productId);
                              if (!selection) return;

                              const location = locationsByProduct[product.productId]?.find(
                                (loc) => loc.id === Number(value),
                              );
                              if (!location) return;

                              const newLocations = [...selection.locations];

                              if (newLocations.length === 0) {
                                // Crear primera ubicación
                                newLocations.push({
                                  locationId: location.id,
                                  locationCode: location.code,
                                  stock: location.stock || 0,
                                  quantity: 0,
                                  type: location.type || null,
                                });
                              } else {
                                // Calcular cantidad ya asignada en otras ubicaciones (excluyendo la primera)
                                const quantityInOtherLocations = newLocations
                                  .slice(1)
                                  .reduce((sum, loc) => sum + loc.quantity, 0);

                                const maxAllowed = Math.max(0, selection.requiredQuantity - quantityInOtherLocations);

                                // Actualizar la primera ubicación
                                newLocations[0] = {
                                  ...newLocations[0],
                                  locationId: location.id,
                                  locationCode: location.code,
                                  stock: location.stock || 0,
                                  quantity: Math.min(newLocations[0].quantity, maxAllowed),
                                  type: location.type || null,
                                };
                              }

                              const newSelection = {
                                ...selection,
                                locations: newLocations,
                              };
                              setProductSelections(new Map(productSelections).set(product.productId, newSelection));
                            }}
                            placeholder={t('modals.prepare_orders.select_location')}
                            dataTestId={`location-select-${product.productId}`}
                          />
                        </div>

                        {/* StepperInput + Max en una fila; en mobile el +/X a la derecha (donde termina el desplegable) */}
                        <div className="flex items-center gap-1 w-full max-md:justify-between">
                          <div className="flex items-center gap-1 flex-1 min-w-0 md:flex-shrink-0">
                            <div className="flex-1 min-w-0 md:flex-none md:w-32 md:flex-shrink-0">
                              <StepperInput
                                value={firstLocation.quantity}
                                onChange={(value) => {
                                  const selection = productSelections.get(product.productId);
                                  if (!selection) return;

                                  const newLocations = [...selection.locations];

                                  // Calcular cantidad ya asignada en otras ubicaciones (excluyendo la primera)
                                  const quantityInOtherLocations = newLocations
                                    .slice(1)
                                    .reduce((sum, loc) => sum + loc.quantity, 0);

                                  // Calcular máximo permitido
                                  const maxAllowed = Math.max(0, selection.requiredQuantity - quantityInOtherLocations);

                                  // Limitar el valor al máximo permitido
                                  const clampedValue = Math.min(value, maxAllowed);

                                  if (newLocations.length === 0) {
                                    // Si no hay ubicaciones, crear una con la ubicación seleccionada
                                    const selectedLoc = locationsByProduct[product.productId]?.find(
                                      (loc) => loc.id === firstLocation.locationId,
                                    );
                                    if (selectedLoc) {
                                      newLocations.push({
                                        locationId: selectedLoc.id,
                                        locationCode: selectedLoc.code,
                                        stock: selectedLoc.stock || 0,
                                        quantity: clampedValue,
                                        type: selectedLoc.type || null,
                                      });
                                    }
                                  } else {
                                    // Actualizar la cantidad de la primera ubicación
                                    newLocations[0] = {
                                      ...newLocations[0],
                                      quantity: clampedValue,
                                    };
                                  }

                                  const newSelection = {
                                    ...selection,
                                    locations: newLocations,
                                  };
                                  setProductSelections(new Map(productSelections).set(product.productId, newSelection));
                                }}
                                min={0}
                                max={maxQuantity}
                                disabled={!selectedLocation || firstLocation.locationId === 0}
                                data-testid={`quantity-stepper-${product.productId}`}
                                className="w-full"
                              />
                            </div>
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-10 w-[60px] flex-shrink-0 text-xs"
                              onClick={() => {
                                if (!selectedLocation) return;
                                const selection = productSelections.get(product.productId);
                                if (!selection) return;

                                const newLocations = [...selection.locations];
                                if (newLocations.length === 0) {
                                  newLocations.push({
                                    locationId: selectedLocation.id,
                                    locationCode: selectedLocation.code,
                                    stock: selectedLocation.stock || 0,
                                    quantity: selection.requiredQuantity,
                                    type: selectedLocation.type || null,
                                  });
                                } else {
                                  // Calcular cantidad ya asignada en otras ubicaciones
                                  const quantityInOtherLocations = newLocations
                                    .slice(1)
                                    .reduce((sum, loc) => sum + loc.quantity, 0);

                                  const maxAllowed = Math.max(0, selection.requiredQuantity - quantityInOtherLocations);

                                  newLocations[0] = {
                                    ...newLocations[0],
                                    quantity: maxAllowed,
                                  };
                                }

                                const newSelection = {
                                  ...selection,
                                  locations: newLocations,
                                };
                                setProductSelections(new Map(productSelections).set(product.productId, newSelection));
                              }}
                              disabled={
                                !selectedLocation ||
                                firstLocation.locationId === 0 ||
                                firstLocation.quantity >= maxQuantity ||
                                maxQuantity === 0
                              }
                            >
                              {t('modals.prepare_orders.max')}
                            </Button>
                          </div>

                          {/* Botón + o X a la derecha (en mobile alineado con el desplegable) */}
                          {product.locations.length >= 2 ? (
                            // Si hay 2 o más, el primer elemento tiene X
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 flex-shrink-0 text-error hover:text-error max-md:ml-auto"
                              onClick={() => handleRemoveLocation(product.productId, 0)}
                              title={t('modals.prepare_orders.remove_location', {
                                defaultValue: 'Eliminar ubicación',
                              })}
                            >
                              <FontAwesomeIcon icon={faTimes} className="w-3 h-3" />
                            </Button>
                          ) : availableLocations.length > Math.max(product.locations.length, 1) ? (
                            // Si solo hay 1, muestra + si hay más disponibles (en mobile a la derecha del Max)
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 flex-shrink-0 max-md:ml-auto"
                              onClick={() => handleAddLocation(product.productId)}
                              title={t('modals.prepare_orders.add_location')}
                            >
                              <FontAwesomeIcon icon={faPlus} className="w-3 h-3" />
                            </Button>
                          ) : (
                            // Espacio reservado solo en desktop para alineación; en mobile no dejar hueco
                            <div className="hidden md:block w-8 h-8 flex-shrink-0" />
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Ubicaciones adicionales (si las hay) - en mobile también apiladas */}
                  {product.locations.length > 1 && (
                    <div className="mt-3 space-y-2">
                      {product.locations.slice(1).map((location, index) => {
                        const isLast = index === product.locations.length - 2;

                        // Obtener ubicaciones ya seleccionadas (excluyendo la actual de esta fila)
                        const selectedLocationIds = new Set(
                          product.locations
                            .map((loc) => loc.locationId)
                            .filter((id) => id > 0 && id !== location.locationId),
                        );

                        // Filtrar ubicaciones excluyendo las ya seleccionadas en otras filas
                        // La ubicación actual de esta fila se incluye automáticamente porque no está en el Set
                        const locOptions = (locationsByProduct[product.productId] || [])
                          .filter((loc) => !selectedLocationIds.has(loc.id))
                          .map((loc) => {
                            const stockText =
                              loc.stock === -1
                                ? t('modals.prepare_orders.unlimited_stock', {
                                    defaultValue: 'Ilimitado',
                                  })
                                : `${loc.stock || 0}un.`;
                            return {
                              label: `${loc.code} (${stockText})`,
                              value: String(loc.id),
                            };
                          });

                        const selectedLoc = locationsByProduct[product.productId]?.find(
                          (loc) => loc.id === location.locationId,
                        );

                        // Calcular cantidad ya asignada en otras ubicaciones (excluyendo la actual)
                        const quantityInOtherLocations = product.locations
                          .filter((loc, idx) => idx !== index + 1)
                          .reduce((sum, loc) => sum + loc.quantity, 0);

                        // El máximo es la cantidad requerida menos lo ya asignado en otras ubicaciones
                        const locMaxQuantity = Math.max(0, product.requiredQuantity - quantityInOtherLocations);

                        return (
                          <div
                            key={`${location.locationId}-${index + 1}`}
                            className="flex flex-col gap-2 w-full max-md:flex-col md:flex-row md:items-center md:gap-4 min-w-0"
                          >
                            {/* Espaciador para alinear con la columna de controles de la primera fila (solo desktop) */}
                            <div className="hidden md:block flex-1 min-w-0" />
                            {/* Misma estructura que la primera fila; sin anchos fijos que causen scroll */}
                            <div className="flex flex-col gap-2 w-full min-w-0 md:w-auto md:flex-row md:items-center md:gap-3 md:flex-shrink-0 md:flex-nowrap">
                              <div className="w-full min-w-0 md:w-64 flex-shrink-0">
                                <Combobox
                                  options={locOptions}
                                  value={String(location.locationId)}
                                  setValue={(value) => handleLocationChange(product.productId, index + 1, value)}
                                  placeholder={t('modals.prepare_orders.select_location')}
                                  dataTestId={`location-select-${product.productId}-${index + 1}`}
                                />
                              </div>
                              {/* Fila stepper + Max + botón: en mobile igual que la primera fila (justify-between, +/X a la derecha) */}
                              <div className="flex items-center gap-1 w-full min-w-0 max-md:justify-between">
                                <div className="flex items-center gap-1 flex-1 min-w-0 md:flex-shrink-0">
                                  <div className="flex-1 min-w-0 md:flex-none md:w-32 md:flex-shrink-0">
                                    <StepperInput
                                      value={location.quantity}
                                      onChange={(value) => {
                                        const newLocations = [...product.locations];

                                        // Calcular cantidad ya asignada en otras ubicaciones (excluyendo la actual)
                                        const quantityInOtherLocations = newLocations
                                          .filter((loc, idx) => idx !== index + 1)
                                          .reduce((sum, loc) => sum + loc.quantity, 0);

                                        // Calcular máximo permitido
                                        const maxAllowed = Math.max(
                                          0,
                                          product.requiredQuantity - quantityInOtherLocations,
                                        );

                                        // Limitar el valor al máximo permitido
                                        const clampedValue = Math.min(value, maxAllowed);

                                        newLocations[index + 1] = {
                                          ...location,
                                          quantity: clampedValue,
                                        };
                                        setProductSelections(
                                          new Map(productSelections).set(product.productId, {
                                            ...product,
                                            locations: newLocations,
                                          }),
                                        );
                                      }}
                                      min={0}
                                      max={locMaxQuantity}
                                      disabled={!selectedLoc}
                                      data-testid={`quantity-stepper-${product.productId}-${index + 1}`}
                                      className="w-full"
                                    />
                                  </div>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="h-10 w-[60px] flex-shrink-0 text-xs"
                                    onClick={() => {
                                      if (!selectedLoc) return;
                                      const selection = productSelections.get(product.productId);
                                      if (!selection) return;

                                      const newLocations = [...selection.locations];

                                      // Calcular cantidad ya asignada en otras ubicaciones
                                      const quantityInOtherLocations = newLocations
                                        .filter((loc, idx) => idx !== index + 1)
                                        .reduce((sum, loc) => sum + loc.quantity, 0);

                                      const maxAllowed = Math.max(
                                        0,
                                        selection.requiredQuantity - quantityInOtherLocations,
                                      );

                                      newLocations[index + 1] = {
                                        ...location,
                                        quantity: maxAllowed,
                                      };

                                      const newSelection = {
                                        ...selection,
                                        locations: newLocations,
                                      };
                                      setProductSelections(
                                        new Map(productSelections).set(product.productId, newSelection),
                                      );
                                    }}
                                    disabled={
                                      !selectedLoc || location.quantity >= locMaxQuantity || locMaxQuantity === 0
                                    }
                                  >
                                    {t('modals.prepare_orders.max')}
                                  </Button>
                                </div>
                                {/* Botones: X para todos excepto el último, + siempre en el último */}
                                {isLast ? (
                                  // Último elemento: botón + siempre visible si hay más disponibles
                                  availableLocations.length > product.locations.length ? (
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="h-8 w-8"
                                      onClick={() => handleAddLocation(product.productId)}
                                      title={t('modals.prepare_orders.add_location')}
                                    >
                                      <FontAwesomeIcon icon={faPlus} className="w-3 h-3" />
                                    </Button>
                                  ) : (
                                    // Espacio reservado solo en desktop; en mobile no dejar hueco
                                    <div className="hidden md:block w-8 h-8 flex-shrink-0" />
                                  )
                                ) : (
                                  // Elementos del medio: botón X
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-8 w-8 text-error hover:text-error"
                                    onClick={() => handleRemoveLocation(product.productId, index + 1)}
                                    title={t('modals.prepare_orders.remove_location', {
                                      defaultValue: 'Eliminar ubicación',
                                    })}
                                  >
                                    <FontAwesomeIcon icon={faTimes} className="w-3 h-3" />
                                  </Button>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div className="flex flex-row justify-end gap-2 pt-4 border-t border-card-border">
            <Button variant="outline" size="lg" onClick={handleCancel}>
              {t('actions.cancel')}
              <EscIcon className="w-4 h-4" />
            </Button>
            <Button variant="primary" size="lg" onClick={handleConfirm}>
              {t('actions.confirm')}
              <EnterIcon className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </ModalCenter>
    );
  },
);

PrepareOrdersModal.displayName = 'PrepareOrdersModal';
